import { useSyncExternalStore, useCallback } from 'react';
import { capture } from '../lib/posthog';
import { purgePlaylistRefs } from './useSchedule';

const PL_KEY = 'melodiza:user-playlists';

// ─── Module-level shared store ─────────────────────────────────────────

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(PL_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

let _playlists = load();
const _listeners = new Set();

function emit() {
  _listeners.forEach((l) => l());
}

function persist() {
  try {
    localStorage.setItem(PL_KEY, JSON.stringify(_playlists));
  } catch (err) {
    console.error('[useUserPlaylists] persist failed:', err);
  }
}

function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function getSnapshot() {
  return _playlists;
}

// Same-tab cross-component refresh trigger (used after direct localStorage writes elsewhere).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== PL_KEY) return;
    _playlists = load();
    emit();
  });
  window.addEventListener('melodiza:user-playlists-changed', () => {
    _playlists = load();
    emit();
  });
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID)
    return crypto.randomUUID();
  return `pl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Public API (module-level mutators that any caller can use) ────────

export function createUserPlaylist(name, trackIds = []) {
  const newPlaylist = {
    id: uuid(),
    name,
    description: '',
    cover: null,
    trackIds: [...new Set(trackIds)],
    createdAt: Date.now(),
  };
  _playlists = [newPlaylist, ..._playlists];
  persist();
  emit();
  capture('playlist_created', {
    playlistId: newPlaylist.id,
    name,
    trackCount: newPlaylist.trackIds.length,
  });
  return newPlaylist;
}

export function renameUserPlaylist(id, name) {
  _playlists = _playlists.map((p) => (p.id === id ? { ...p, name } : p));
  persist();
  emit();
}

export function updateUserPlaylistMeta(id, patch) {
  const allowed = ['name', 'description', 'cover'];
  const filtered = {};
  for (const k of allowed) if (k in patch) filtered[k] = patch[k];
  _playlists = _playlists.map((p) => (p.id === id ? { ...p, ...filtered } : p));
  persist();
  emit();
}

export function deleteUserPlaylist(id) {
  _playlists = _playlists.filter((p) => p.id !== id);
  persist();
  emit();
  purgePlaylistRefs(id);
}

export function addTrackToUserPlaylist(id, trackId) {
  _playlists = _playlists.map((p) =>
    p.id === id ? { ...p, trackIds: [...new Set([...p.trackIds, trackId])] } : p
  );
  persist();
  emit();
}

export function removeTrackFromUserPlaylist(id, trackId) {
  _playlists = _playlists.map((p) =>
    p.id === id
      ? { ...p, trackIds: p.trackIds.filter((t) => t !== trackId) }
      : p
  );
  persist();
  emit();
}

export function reorderUserPlaylist(id, trackIds) {
  _playlists = _playlists.map((p) => (p.id === id ? { ...p, trackIds } : p));
  persist();
  emit();
}

// ─── Public snapshot accessor (used by useScheduleEngine) ─────────────

export function getUserPlaylistsSnapshot() {
  return _playlists;
}

// ─── Test helper ───────────────────────────────────────────────────────

export function __resetForTests() {
  _playlists = load();
  _listeners.clear();
}

// ─── Hook ──────────────────────────────────────────────────────────────

export function useUserPlaylists() {
  const playlists = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const create = useCallback((name) => {
    const newPl = createUserPlaylist(name);
    return newPl;
  }, []);

  const rename = useCallback((id, name) => renameUserPlaylist(id, name), []);
  const updateMeta = useCallback(
    (id, patch) => updateUserPlaylistMeta(id, patch),
    []
  );
  const del = useCallback((id) => deleteUserPlaylist(id), []);
  const addTrack = useCallback(
    (id, trackId) => addTrackToUserPlaylist(id, trackId),
    []
  );
  const removeTrack = useCallback(
    (id, trackId) => removeTrackFromUserPlaylist(id, trackId),
    []
  );
  const reorder = useCallback(
    (id, trackIds) => reorderUserPlaylist(id, trackIds),
    []
  );

  return {
    playlists,
    create,
    rename,
    updateMeta,
    delete: del,
    addTrack,
    removeTrack,
    reorder,
  };
}
